import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/page-header';
import SettingsLayout from '@/components/settings-layout';
import { setWebTitle } from '@/lib/format-utils';
import {
    Stack,
    Group,
    Button,
    NumberInput,
    Select,
    ActionIcon,
    TextInput
} from '@mantine/core';
import {
    IconPlus,
    IconTrash,
    IconPercentage,
    IconPencil
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { DiscountCategory, Discount } from '@turizi/shared';
import {
    useDiscountCategories,
    useCreateDiscountCategory,
    useDeleteDiscountCategory,
    useUpdateDiscountCategory
} from '@/hooks/api-hooks';
import { SkeletonList } from '@/components/snippets';
import CardModal from '@/components/card-modal';

const EMPTY_DISCOUNT_CATEGORY_FORM: Partial<DiscountCategory> = {
    category: '',
    discounts: [],
};

export default function SettingsDiscountCategoriesPage(): JSX.Element {
    const { t, i18n } = useTranslation();
    const [modalOpened, setModalOpened] = useState(false);
    const [editingCategory, setEditingCategory] = useState<DiscountCategory | null>(null);
    const [categoryForm, setCategoryForm] = useState(EMPTY_DISCOUNT_CATEGORY_FORM);

    setWebTitle(t('discount_categories'));

    const {
        data: discountCategoriesData,
        isLoading: isLoadingDiscountCategories
    } = useDiscountCategories();
    const createDiscountCategoryMutation = useCreateDiscountCategory();
    const updateDiscountCategoryMutation = useUpdateDiscountCategory();
    const deleteDiscountCategoryMutation = useDeleteDiscountCategory();

    const discountCategories = discountCategoriesData?.data || [];

    const currencies = Object.keys((i18n.t('currencies', { returnObjects: true }) as any) || {});

    const getCurrencyLabel = (code: string) => {
        return i18n.exists(`currencies.${code}`)
            ? t(`currencies.${code}`)
            : code;
    };

    const handleOpenModal = (category?: DiscountCategory) => {
        if (category) {
            setEditingCategory(category);
            setCategoryForm(category);
        } else {
            setEditingCategory(null);
            setCategoryForm(EMPTY_DISCOUNT_CATEGORY_FORM);
        }
        setModalOpened(true);
    };

    const handleCloseModal = () => {
        setModalOpened(false);
        setEditingCategory(null);
        setCategoryForm(EMPTY_DISCOUNT_CATEGORY_FORM);
    };

    const handleSaveCategory = () => {
        const mutation = editingCategory ? updateDiscountCategoryMutation : createDiscountCategoryMutation;

        mutation.mutate(categoryForm, {
            onSuccess: () => {
                handleCloseModal();
            },
            onError: (error: any) => {
                notifications.show({
                    title: t('error'),
                    message: error?.message || t('error'),
                    color: 'red',
                });
            }
        });
    };

    const handleDeleteCategory = (id: number) => {
        if (confirm(t('confirm_delete'))) {
            deleteDiscountCategoryMutation.mutate(id);
        }
    };

    const handleAddDiscount = () => {
        const newDiscount: Partial<Discount> = {
            type: 'percentage',
            currency: '',
            category: categoryForm.category || '',
            percentage: 0,
        };
        setCategoryForm({
            ...categoryForm,
            discounts: [...(categoryForm.discounts || []), newDiscount as Discount],
        });
    };

    const handleRemoveDiscount = (index: number) => {
        const newDiscounts = [...(categoryForm.discounts || [])];
        newDiscounts.splice(index, 1);
        setCategoryForm({
            ...categoryForm,
            discounts: newDiscounts,
        });
    };

    const handleUpdateDiscount = (index: number, field: keyof Discount, value: any) => {
        const newDiscounts = [...(categoryForm.discounts || [])];
        newDiscounts[index] = { ...newDiscounts[index], [field]: value };
        setCategoryForm({
            ...categoryForm,
            discounts: newDiscounts,
        });
    };

    return (
        <div>
            <PageHeader title={t('settings')} />

            <div className="panel-content max-w-screen-lg">
                <SettingsLayout>
                    <div className='flex justify-between items-center mb-6'>
                        <div>
                            <div className='font-semibold text-gray-800 text-2xl mb-2'>
                                {t('discount_categories')}
                            </div>
                            <div className='text-gray-700 text-sm mb-6'>
                                {t('discount_categories_description')}
                            </div>
                        </div>
                        <Button
                            onClick={() => handleOpenModal()}
                            size="sm"
                            color='black'
                            radius="sm"
                        >
                            {t('add_discount_category')}
                        </Button>
                    </div>

                    <div className='solid-box-border'>
                        <div className='subtle-table'>
                            <table>
                                <thead>
                                    <tr>
                                        <th>{t('name')}</th>
                                        <th>{t('discounts')}</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {discountCategories.map((category) => (
                                        <tr key={category.id}>
                                            <td>
                                                <div className="text-md font-semibold">
                                                    {category.category}
                                                </div>
                                            </td>
                                            <td>
                                                {category.discounts?.map((discount, idx) => (
                                                    <div key={idx} className="text-sm mb-1">
                                                        {discount.currency}: <span className="font-semibold">{discount.percentage}</span>%
                                                    </div>
                                                ))}
                                            </td>
                                            <td>
                                                <Group gap="xs" justify="flex-end">
                                                    <ActionIcon
                                                        variant="subtle"
                                                        color="gray"
                                                        onClick={() => handleOpenModal(category)}
                                                    >
                                                        <IconPencil size={16} />
                                                    </ActionIcon>
                                                    <ActionIcon
                                                        variant="subtle"
                                                        color="red"
                                                        loading={deleteDiscountCategoryMutation.isPending}
                                                        onClick={() => handleDeleteCategory(category.id)}
                                                    >
                                                        <IconTrash size={16} />
                                                    </ActionIcon>
                                                </Group>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {isLoadingDiscountCategories && <SkeletonList />}
                    </div>

                    {/* Discount Category Modal */}
                    <CardModal
                        opened={modalOpened}
                        onClose={handleCloseModal}
                        size="lg"
                        header={editingCategory ? t('edit') + ' ' + t('discount_category') : t('add') + ' ' + t('discount_category')}
                        footer={
                            <div className="flex justify-end gap-2">
                                <Button
                                    variant="subtle"
                                    size='sm'
                                    color='black'
                                    onClick={handleCloseModal}>
                                    {t('cancel')}
                                </Button>
                                <Button
                                    size='sm'
                                    color='black'
                                    onClick={handleSaveCategory}
                                    loading={createDiscountCategoryMutation.isPending || updateDiscountCategoryMutation.isPending}
                                >
                                    {t('save')}
                                </Button>
                            </div>
                        }
                    >
                        <div className="space-y-4">
                            <TextInput
                                label={t('name')}
                                className='font-bold'
                                value={categoryForm.category}
                                onChange={(e) => setCategoryForm({ ...categoryForm, category: e.target.value })}
                                disabled={editingCategory !== null}
                            />

                            <div>
                                <div className="font-semibold">
                                    {t('default_discounts')}
                                </div>
                                <div className="text-sm text-gray-500 italic">
                                    {t('default_discounts_description')}
                                </div>
                            </div>
                            {categoryForm.discounts?.map((discount, index) => (
                                <Group key={index} align="flex-end">
                                    <Select
                                        label={t('currency')}
                                        value={discount.currency}
                                        onChange={(value) => handleUpdateDiscount(index, 'currency', value)}
                                        data={currencies.map(code => ({
                                            value: code,
                                            label: getCurrencyLabel(code)
                                        }))}
                                        style={{ flex: 1 }}
                                        searchable
                                    />
                                    <NumberInput
                                        label={t('percentage')}
                                        value={discount.percentage}
                                        onChange={(value) => handleUpdateDiscount(index, 'percentage', Number(value))}
                                        min={0}
                                        max={100}
                                        style={{ flex: 1 }}
                                        rightSection={<IconPercentage size={16} />}
                                    />
                                    <ActionIcon
                                        color="red"
                                        variant="subtle"
                                        onClick={() => handleRemoveDiscount(index)}
                                        size="lg"
                                    >
                                        <IconTrash size={16} />
                                    </ActionIcon>
                                </Group>
                            ))}
                            <Button
                                size="xs"
                                variant="subtle"
                                leftSection={<IconPlus size={16} />}
                                onClick={handleAddDiscount}
                                color='black'
                            >
                                {t('add_discount')}
                            </Button>
                        </div>
                    </CardModal>
                </SettingsLayout>
            </div>
        </div>
    );
}
